import { AddressFieldMaxLength } from "@shared";
import { TFunction } from "i18next";
import * as Yup from "yup";

import { CourtDayParam, CourtDayResponse, CourtDayType, CourtResponse, CreateCourtRequest, DayOfWeek } from "@/api";
import { ApiErrorCode, getApiErrorCode } from "@/common/helper/apiErrorHelper";
import { nameof } from "@/common/helper/commonHelper";
import { DigitsPattern, ZipCodePattern } from "@/common/helper/validationPatterns";

import {
  CourtDayFormValue,
  CourtFileNumberPrefixLength,
  CourtFormType,
  CourtMaxLength,
  CourtZipCodeLength,
} from "./CourtForm.types";

// MUI X DatePicker emits an Invalid Date (not null) for incomplete input, so drop those before saving.
function isValidDate(date: Date | null): date is Date {
  return date != null && !isNaN(date.getTime());
}

export function emptyCourtDay(): CourtDayFormValue {
  return {
    type: CourtDayType.Weekday,
    weekday: DayOfWeek.Monday,
    ordinal: null,
    dates: [null],
  };
}

function courtDayResponseToFormValue(day: CourtDayResponse): CourtDayFormValue {
  return {
    type: day.type,
    weekday: day.weekday,
    ordinal: day.ordinal,
    dates: day.dates.length > 0 ? [...day.dates] : [null],
  };
}

export function courtToFormValues(court: CourtResponse): CourtFormType {
  return {
    name: court.name,
    shortCode: court.shortCode,
    fileNumberPrefix: court.fileNumberPrefix ?? "",
    street: court.street ?? "",
    houseNumber: court.houseNumber ?? "",
    zipCode: court.zipCode,
    city: court.city ?? "",
    country: court.country ?? "",
    districts: [...court.districts],
    courtDays: court.courtDays.map(courtDayResponseToFormValue),
  };
}

export function emptyCourtFormValues(): CourtFormType {
  return {
    name: "",
    shortCode: "",
    fileNumberPrefix: "",
    street: "",
    houseNumber: "",
    zipCode: "",
    city: "",
    country: "",
    districts: [],
    courtDays: [],
  };
}

function toCourtDayParam(day: CourtDayFormValue): CourtDayParam {
  const isRule = day.type === CourtDayType.Rule;
  const isWeekday = day.type === CourtDayType.Weekday;
  const isSingleDates = day.type === CourtDayType.SingleDates;

  return {
    type: day.type,
    weekday: isWeekday || isRule ? day.weekday : null,
    ordinal: isRule ? day.ordinal : null,
    dates: isSingleDates ? day.dates.filter(isValidDate) : [],
  };
}

export function formValuesToRequest(values: CourtFormType): CreateCourtRequest {
  return {
    name: values.name.trim(),
    shortCode: values.shortCode.trim(),
    fileNumberPrefix: values.fileNumberPrefix.trim(),
    street: values.street.trim() || null,
    houseNumber: values.houseNumber.trim() || null,
    zipCode: values.zipCode.trim(),
    city: values.city.trim() || null,
    country: values.country.trim() || null,
    districts: values.districts,
    courtDays: values.courtDays.map(toCourtDayParam),
  };
}

export function courtValidationSchema(t: TFunction) {
  const requiresWeekday = (type: CourtDayType) => type === CourtDayType.Weekday || type === CourtDayType.Rule;

  return Yup.object().shape({
    name: Yup.string()
      .trim()
      .required(t("common.validation.fieldRequired", { fieldName: t("settings.courts.form.name") }))
      .max(CourtMaxLength.name, ({ max }) => t("common.validation.maxLength", { max })),
    shortCode: Yup.string()
      .trim()
      .required(t("common.validation.fieldRequired", { fieldName: t("settings.courts.form.shortCode") }))
      .max(CourtMaxLength.shortCode, ({ max }) => t("common.validation.maxLength", { max })),
    fileNumberPrefix: Yup.string()
      .trim()
      .required(t("common.validation.fieldRequired", { fieldName: t("settings.courts.form.fileNumberPrefix") }))
      .matches(DigitsPattern, t("settings.courts.form.fileNumberPrefixInvalid"))
      .length(CourtFileNumberPrefixLength, t("settings.courts.form.fileNumberPrefixInvalid")),
    street: Yup.string().max(AddressFieldMaxLength.street, ({ max }) => t("common.validation.maxLength", { max })),
    houseNumber: Yup.string().max(AddressFieldMaxLength.houseNumber, ({ max }) =>
      t("common.validation.maxLength", { max })
    ),
    zipCode: Yup.string()
      .trim()
      .required(t("common.validation.fieldRequired", { fieldName: t("common.address.zipCode") }))
      .matches(ZipCodePattern, t("settings.courts.form.zipCodeInvalid"))
      .length(CourtZipCodeLength, t("settings.courts.form.zipCodeInvalid")),
    city: Yup.string().max(AddressFieldMaxLength.city, ({ max }) => t("common.validation.maxLength", { max })),
    country: Yup.string().max(AddressFieldMaxLength.country, ({ max }) => t("common.validation.maxLength", { max })),
    districts: Yup.array().min(
      1,
      t("common.validation.fieldRequired", { fieldName: t("settings.courts.form.districts") })
    ),
    courtDays: Yup.array().of(
      Yup.object().shape({
        type: Yup.mixed<CourtDayType>()
          .nullable()
          .required(t("common.validation.fieldRequired", { fieldName: t("settings.courts.gerichtstag.type") })),
        weekday: Yup.mixed<DayOfWeek>()
          .nullable()
          .when("type", {
            is: (type: CourtDayType) => requiresWeekday(type),
            then: (schema) =>
              schema.required(
                t("common.validation.fieldRequired", { fieldName: t("settings.courts.gerichtstag.weekday") })
              ),
          }),
        ordinal: Yup.mixed()
          .nullable()
          .when("type", {
            is: CourtDayType.Rule,
            then: (schema) =>
              schema.required(
                t("common.validation.fieldRequired", { fieldName: t("settings.courts.gerichtstag.rule") })
              ),
          }),
        dates: Yup.array().when("type", {
          is: CourtDayType.SingleDates,
          then: (schema) =>
            schema.test(
              "has-date",
              t("common.validation.fieldRequired", { fieldName: t("settings.courts.gerichtstag.date") }),
              (dates) => (dates ?? []).some(isValidDate)
            ),
        }),
      })
    ),
  });
}

/** A prefix clash lands on its field; any other failed save is reported as a toast. */
export async function applyCourtSaveError(
  error: unknown,
  setFieldError: (field: string, message: string | undefined) => void,
  showError: (message: string) => void,
  t: TFunction
) {
  const code = await getApiErrorCode(error);
  if (code === ApiErrorCode.CourtFileNumberPrefixAlreadyInUse) {
    setFieldError(nameof<CourtFormType>("fileNumberPrefix"), t("settings.courts.form.fileNumberPrefixAlreadyInUse"));
    return;
  }
  showError(
    code === ApiErrorCode.CourtDistrictAlreadyAssigned
      ? t("settings.courts.districtAlreadyAssigned")
      : t("settings.courts.saveFailed")
  );
}
